import { useContext, useState } from "react";
import { Alert, Col, Row, Tab, Tabs } from "react-bootstrap";
import { useParams, useSearchParams } from "react-router-dom";
import type { AccountsController } from "../../../../api/src/accounts/accounts.controller";
import type { UnpackResponse } from "../../../../api/src/lib";
import Loading from "../../Loading";
import TaxPerformancePeriodSelector from "../../components/TaxPerformancePeriodSelector";
import {
  deserializePerformancePercents,
  useAuthenticatedPercentFetch,
} from "../percents";
import { formatCurrencyComponent, formatPercent } from "../../lib/numbers";
import { isRothAccountType } from "../../lib/tax-registration";
import { AccountContext } from "./AccountInfo";
import AccountTaxTable from "./AccountTaxTable";
import RMDLedger from "../household/RMDLedger";
import { useQueryRmdAnnuals, useQueryRmdProfiles } from "../household/lib";
// Query-param key for the Tax sub-tab so reloads preserve the selected tab.
// Tab keys here are "rmd" and "tax" (default when RMD profile is present is
// "rmd"; otherwise only the summary view renders and the param is a no-op).
const TAB_PARAM = "tab";

const AccountTax = () => {
  const { account } = useContext(AccountContext);
  const { accountId } = useParams();
  const [customTaxPeriodParams, setCustomTaxPeriodParams] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  // Check if this account has an RMD profile: every page of
  // `GET .../rmd/profiles` (APP-1653). With no household, the walk is held
  // on the placeholder path `/v1/households/0/rmd/profiles` and asks for
  // nothing. Were it asked, `getProfiles` would answer 404 (no household 0
  // of the caller's firm).
  const householdId = account?.householdId;
  const { data: profiles } = useQueryRmdProfiles(householdId || 0, {
    enabled: !!householdId,
  });
  const rmdProfile = profiles?.find(
    (p) => p.accountId === parseInt(accountId ?? "0"),
  );
  // Roth 401k balances are post-tax and exempt from lifetime RMDs under
  // SECURE 2.0. Never show the RMD tab on a Roth 401k even if a stray
  // profile exists in the database.
  const hasRMD = !!rmdProfile && !isRothAccountType(account?.type);

  const {
    data: dataAccountTaxPerformance,
    isPending: isPendingAccountTaxPerformance,
    isError: isErrorAccountTaxPerformance,
  } = useAuthenticatedPercentFetch<
    UnpackResponse<AccountsController["getAccountPerformance"]>
  >(
    `/v1/accounts/${accountId}/performance?${customTaxPeriodParams}`,
    deserializePerformancePercents,
  );
  const taxPerformance = dataAccountTaxPerformance?.data;

  const taxSummary =
    typeof account === "undefined" || typeof taxPerformance === "undefined" ? (
      <Loading />
    ) : (
      <>
        <Row className="mb-2">
          <Col xl={2} md={2}>
            <TaxPerformancePeriodSelector
              inceptionDate={account.inceptionDate}
              setCustomPeriodParams={setCustomTaxPeriodParams}
            />
          </Col>
          {isPendingAccountTaxPerformance ? (
            <Loading message="Tax Details" />
          ) : isErrorAccountTaxPerformance ? (
            <Alert variant="danger">Failed to load tax details</Alert>
          ) : (
            <>
              <Col md={4}>
                <Row>
                  <Col as="dt" md={7} sm={8} xs={8}>
                    Total Taxable Div./Int.
                  </Col>
                  <Col as="dd" md={5} sm={4} xs={4}>
                    {formatCurrencyComponent(taxPerformance.income, 2)} (
                    {formatPercent(
                      taxPerformance.balance === 0
                        ? 0
                        : taxPerformance.income / taxPerformance.balance,
                      2,
                    )}
                    )
                  </Col>
                </Row>
                <Row>
                  <Col as="dt" md={7} sm={8} xs={8}>
                    TLH Opportunities
                  </Col>
                  <Col as="dd" md={5} sm={4} xs={4}>
                    {formatCurrencyComponent(account.unrealizedLoss ?? 0, 2)} (
                    {formatPercent(
                      taxPerformance.balance === 0
                        ? 0
                        : (account.unrealizedLoss ?? 0) /
                            taxPerformance.balance,
                      2,
                    )}
                    )
                  </Col>
                </Row>
              </Col>
              <Col md={6}>
                <Row>
                  <Col as="dt" md={8} sm={8} xs={8}>
                    Taxable Realized LT G/L
                  </Col>
                  <Col as="dd">
                    {formatCurrencyComponent(
                      taxPerformance.realizedGainLossLT,
                      2,
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col as="dt" sm={8} xs={8}>
                    Taxable Realized ST G/L
                  </Col>
                  <Col as="dd">
                    {formatCurrencyComponent(
                      taxPerformance.realizedGainLossST,
                      2,
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col as="dt" sm={8} xs={8}>
                    Total Taxable Realized G/L
                  </Col>
                  <Col as="dd">
                    {formatCurrencyComponent(
                      taxPerformance.realizedGainLossLT +
                        taxPerformance.realizedGainLossST,
                      2,
                    )}
                  </Col>
                </Row>
              </Col>
            </>
          )}
        </Row>
        <Row>
          <Col>
            <h3>Account Holdings</h3>
            <AccountTaxTable customTaxPeriodParams={customTaxPeriodParams} />
          </Col>
        </Row>
      </>
    );

  if (!hasRMD) return taxSummary;

  const activeTab = searchParams.get(TAB_PARAM) === "rmd" ? "rmd" : "tax";
  const handleTabSelect = (key: string | null) => {
    if (!key) return;
    const next = new URLSearchParams(searchParams);
    next.set(TAB_PARAM, key);
    setSearchParams(next, { replace: true });
  };

  return (
    <Tabs activeKey={activeTab} onSelect={handleTabSelect} className="mb-3">
      <Tab eventKey="rmd" title="RMD Tracking">
        <AccountRMD householdId={householdId!} profileId={rmdProfile!.id} />
      </Tab>
      <Tab eventKey="tax" title="Tax Summary">
        {taxSummary}
      </Tab>
    </Tabs>
  );
};

/**
 * AccountRMD — RMD detail view scoped to a single account's profile.
 * Shows metrics, progress, and distribution ledger.
 */
const AccountRMD = ({
  householdId,
  profileId,
}: {
  householdId: number;
  profileId: number;
}) => {
  const CY = new Date().getFullYear();

  // Every page of `GET .../rmd/profiles` and of
  // `GET .../rmd/profiles/:profileId/annuals` (APP-1653).
  const { data: profiles } = useQueryRmdProfiles(householdId);
  const profile = profiles?.find((p) => p.id === profileId);

  const { data: annuals } = useQueryRmdAnnuals(householdId, profileId);
  const currentAnnual = annuals?.find((a) => a.year === CY);

  if (!profile) return <Loading />;

  const pct = currentAnnual
    ? Math.min(
        100,
        (currentAnnual.distributedAmount / currentAnnual.requiredAmount) * 100,
      )
    : 0;

  return (
    <>
      {currentAnnual && (
        <>
          <Row className="g-2 mb-3">
            <Col>
              <div className="border rounded p-2 text-center">
                <div className="text-muted small">Required RMD</div>
                <div className="fw-bold text-primary">
                  {formatCurrencyComponent(currentAnnual.requiredAmount, 2)}
                </div>
              </div>
            </Col>
            <Col>
              <div className="border rounded p-2 text-center">
                <div className="text-muted small">Distributed</div>
                <div className="fw-bold">
                  {formatCurrencyComponent(currentAnnual.distributedAmount, 2)}
                </div>
              </div>
            </Col>
            <Col>
              <div className="border rounded p-2 text-center">
                <div className="text-muted small">Remaining</div>
                <div
                  className={`fw-bold ${currentAnnual.remainingAmount > 0 ? "text-danger" : "text-success"}`}
                >
                  {formatCurrencyComponent(currentAnnual.remainingAmount, 2)}
                </div>
              </div>
            </Col>
            <Col>
              <div className="border rounded p-2 text-center">
                <div className="text-muted small">QCD YTD</div>
                <div className="fw-bold">
                  {formatCurrencyComponent(currentAnnual.qcdAmount, 2)}
                </div>
              </div>
            </Col>
          </Row>
          <div className="progress mb-3" style={{ height: 8 }}>
            <div
              className={`progress-bar ${pct >= 100 ? "bg-success" : "bg-primary"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </>
      )}
      {!currentAnnual && (
        <Alert variant="info" className="mb-3">
          No {CY} annual record yet. Set the prior year-end balance from the
          household RMD tab to calculate this year&apos;s RMD.
        </Alert>
      )}
      {currentAnnual && profile.accountId && (
        <RMDLedger
          householdId={householdId}
          profileId={profile.id}
          annualId={currentAnnual.id}
          requiredAmount={currentAnnual.requiredAmount}
          manualDistributions={currentAnnual.distributions}
        />
      )}
    </>
  );
};

export default AccountTax;
